<template>
  <div class="BaseAutocompleteInput">
    <label :for="id">{{ label }}</label>

    <v-autocomplete :id="id" ref="$field" :aria-describedby="undefined" :placeholder="placeholder" :items="items"
      v-model="value" menu-icon="" :prepend-inner-icon="icon" auto-select-first :rules="rules" :hide-details="true"
      :hide-no-data="true" :loading="loading" :disabled="!editable" @update:search="emit('update', $event)"
      :custom-filter="customFilter" autocomplete="off" />

    <ErrorMessageComponent :messages="fieldValidationErrorMessage" />
  </div>
</template>

<script setup lang="ts">
import { useValidationError } from '@/modules/core/composables/validationErrorsComposable'
import type { ValidationRuleType } from '@/modules/core/models/types'
import { nextTick, onBeforeUnmount, onMounted } from 'vue'
import {
  endDuplicatedLabelControl,
  startDuplicatedLabelControl
} from '../../composables/accesibilityComposable'
import ErrorMessageComponent from '../formFields/ErrorMessageComponent.vue'

interface PropsInterface {
  id: string
  modelValue: string | null
  label?: string
  items?: string[]
  placeholder?: string
  rules?: ValidationRuleType[]
  loading?: boolean
  editable?: boolean
  icon?: string
}

const props = withDefaults(defineProps<PropsInterface>(), {
  items: () => [],
  rules: () => [],
  loading: false,
  editable: true
})

const emit = defineEmits(['update'])

const value = defineModel<string | null>()

function customFilter(itemTitle: string, queryText: string, item: any) {
  const text = item.raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/([aeiouAEIOU])[\u0300-\u036f]/g, '$1')

  const searchText = queryText
    .toLowerCase()
    .normalize('NFD')
    .replace(/([aeiouAEIOU])[\u0300-\u036f]/g, '$1')

  return text.indexOf(searchText) > -1
}

const { $field, fieldValidationErrorMessage } = useValidationError()

let observer: MutationObserver | null = null

onMounted(async () => {
  // Esperar render inicial
  await nextTick()
  const rootElement = document.getElementById(props.id)?.parentElement
    ?.parentElement as HTMLElement | null
  observer = startDuplicatedLabelControl(rootElement)
})

onBeforeUnmount(() => {
  endDuplicatedLabelControl(observer)
})
</script>

<style lang="scss" scoped>
.BaseAutocompleteInput {
  display: flex;
  flex-direction: column;
  gap: $gap-s;

  label {
    font-family: $font-family-primary;
    font-size: $font-size-m;
    font-style: normal;
    font-weight: $font-weight-400;
    line-height: $line-height-100p;
    color: $color-neutral-dark;
  }

  :deep(.v-field) {
    background-color: $color-grey-200;
    border-radius: $b-radius-0;
    box-shadow: $box-shadow-0;
    transition: all 0.2s ease-in-out;

    &:focus-within {
      box-shadow: $box-shadow-focus;
    }

    .v-field__input input {
      &:focus,
      &:focus-visible {
        outline: none;
        box-shadow: $box-shadow-none;
      }
    }

    .v-field__prepend-inner {
      i::before {
        font-family: $font-family-icons;
      }
    }

    .v-field__input {
      font-family: $font-family-primary;
      color: $color-neutral-dark;

      &::placeholder {
        color: $color-neutral;
        opacity: 1;
      }
    }

    &.v-field--disabled {
      opacity: 1;

      .v-combobox__selection-text {
        color: $color-neutral;
      }

      .v-field__input {
        input {
          border: none;
        }
      }
    }

    &.v-field--error {
      background-color: $color-error-200;

      .v-field__input {
        color: $color-error;

        &::placeholder {
          color: $color-neutral;
          opacity: 1;
        }
      }
    }
  }
}
</style>
